import React, { useEffect, useState } from "react";
import { Col, Card, CardBody, CardHeader, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import useChartColors from "Components/Common/useChartColors";
import {
  getUserActivityTrend,
  getUserActivityStats,
  getDDSearches,
  getUserActivities,
} from "common/services/analytics.service";

interface UserStats {
  totalUsers: number;
  totalPages: number;
  ddSearches: number;
  bulkSearches: number;
  totalAgencies: number;
}

const ActiveUserStats = ({ chartId }: any) => {
  const [period, setPeriod] = useState("1W");
  const [chartData, setChartData] = useState<any>({
    series: [
      {
        name: "User Sessions",
        data: [],
      },
    ],
    categories: [],
  });

  useEffect(() => {
    // Fetch user activity trend data
    const fetchUserActivityTrendData = async () => {
      let typeOfRequest = "";
      if (period === "1W") typeOfRequest = "pastweek";
      else if (period === "1M") typeOfRequest = "pastmonth";
      else if (period === "6M") typeOfRequest = "past6months";
      else if (period === "1Y") typeOfRequest = "pastyear";
      else if (period === "ALL") typeOfRequest = "all";

      try {
        const response: any = await getUserActivityTrend({ typeOfRequest });

        if (response && response.data) {
          // Extract users data and dates from response
          const usersData = response.data.map((item: any) => item.users);
          const categories = response.data.map((item: any) => {
            // Format date based on the selected period
            if (period === "1W") {
              // For 1 week: show day of the week (Mon, Tue, Wed, etc.)
              const date = new Date(item.date);
              return date.toLocaleDateString("en-US", {
                weekday: "short",
              });
            } else if (period === "1M") {
              // For 1 month: show dates and month (Jan 15, Feb 20, etc.)
              const date = new Date(item.date);
              return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            } else if (period === "6M" || period === "1Y") {
              // For 6 months or 1 year: show months (Jan 2025, Feb 2025, etc.)
              const date = new Date(item.date + "-01");
              return date.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              });
            } else if (period === "ALL") {
              // For ALL: show years (2023, 2024, 2025, etc.)
              const date = new Date(item.date + "-01-01");
              return date.toLocaleDateString("en-US", {
                year: "numeric",
              });
            } else {
              // Default fallback
              const date = new Date(item.date + "-01");
              return date.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              });
            }
          });

          setChartData({
            series: [
              {
                name: "User Sessions",
                data: usersData,
              },
            ],
            categories: categories,
          });
        }
      } catch (error) {
        console.error("Error fetching user activity trend data:", error);
      }
    };
    fetchUserActivityTrendData();
  }, [period]);

  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    totalPages: 0,
    ddSearches: 0,
    bulkSearches: 0,
    totalAgencies: 0,
  });

  // Helper function to generate date ranges based on selected period
  const getDateRange = (
    period: string
  ): { startDate: string; endDate: string } => {
    const now = new Date();
    const endDate = now.toISOString();
    let startDate = "";

    switch (period) {
      case "1W":
        // 1 week ago from now
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(now.getDate() - 7);
        startDate = oneWeekAgo.toISOString();
        break;
      case "1M":
        // 1 month ago from now
        const oneMonthAgo = new Date(now);
        oneMonthAgo.setMonth(now.getMonth() - 1);
        startDate = oneMonthAgo.toISOString();
        break;
      case "6M":
        // 6 months ago from now
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        startDate = sixMonthsAgo.toISOString();
        break;
      case "1Y":
        // 1 year ago from now
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(now.getFullYear() - 1);
        startDate = oneYearAgo.toISOString();
        break;
      case "ALL":
        // Set to a very early date for "all time"
        startDate = new Date("2020-01-01").toISOString();
        break;
      default:
        // Default to 1 week
        const defaultWeekAgo = new Date(now);
        defaultWeekAgo.setDate(now.getDate() - 7);
        startDate = defaultWeekAgo.toISOString();
        break;
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    const fetchUserActivityStatsData = async () => {
      let typeOfRequest = "";
      if (period === "1W") typeOfRequest = "pastweek";
      else if (period === "1M") typeOfRequest = "pastmonth";
      else if (period === "6M") typeOfRequest = "past6months";
      else if (period === "1Y") typeOfRequest = "pastyear";
      else if (period === "ALL") typeOfRequest = "all";

      const response: any = await getUserActivityStats({
        typeOfRequest: typeOfRequest,
      });
      if (response && response.data) {
        setUserStats((state: UserStats) => ({
          ...state,
          totalUsers: response.data.totalUsers,
          totalPages: response.data.totalPages,
          totalAgencies: response.data.totalAgencies,
        }));
      }
    };
    fetchUserActivityStatsData();

    const fetchDDSearches = async () => {
      const { startDate, endDate } = getDateRange(period);

      const response: any = await getDDSearches({
        filters: {
          createdAt: {
            $gte: startDate,
            $lte: endDate,
          },
        },
      });
      if (response && response.meta) {
        const ddSearchesCount = response.meta.pagination.total;
        setUserStats((state: UserStats) => ({
          ...state,
          ddSearches: ddSearchesCount,
        }));
      }
    };
    fetchDDSearches();

    const fetchBulkSearches = async () => {
      const { startDate, endDate } = getDateRange(period);

      const response: any = await getUserActivities({
        filters: {
          frontendPage: {
            name: "Bulk Search Report",
          },
          createdAt: {
            $gte: startDate,
            $lte: endDate,
          },
        },
      });
      if (response && response.meta) {
        const bulkSearchesCount = response.meta.pagination.total;
        setUserStats((state: UserStats) => ({
          ...state,
          bulkSearches: bulkSearchesCount,
        }));
      }
    };
    fetchBulkSearches();
  }, [period]);

  var applicationsStatisticColors = useChartColors(chartId);

  const options: any = {
    chart: {
      height: 345,
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: applicationsStatisticColors,

    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [3],
      curve: "smooth",
      dashArray: [0],
    },
    markers: {
      size: 0,

      hover: {
        sizeOffset: 6,
      },
    },
    xaxis: {
      categories: chartData.categories,
    },
    grid: {
      borderColor: "#f1f1f1",
    },
  };

  return (
    <React.Fragment>
      <Col xxl={8}>
        <Card className="card-height-100">
          <CardHeader className="border-0 align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">Active User Stats</h4>
            <div>
              <button
                type="button"
                className={`btn ${
                  period === "1W" ? "btn-soft-primary" : "btn-soft-secondary"
                } btn-sm material-shadow-none me-1`}
                onClick={() => setPeriod("1W")}
              >
                1W
              </button>
              <button
                type="button"
                className={`btn ${
                  period === "1M" ? "btn-soft-primary" : "btn-soft-secondary"
                } btn-sm material-shadow-none me-1`}
                onClick={() => setPeriod("1M")}
              >
                1M
              </button>
              <button
                type="button"
                className={`btn ${
                  period === "6M" ? "btn-soft-primary" : "btn-soft-secondary"
                } btn-sm material-shadow-none me-1`}
                onClick={() => setPeriod("6M")}
              >
                6M
              </button>
              <button
                type="button"
                className={`btn ${
                  period === "1Y" ? "btn-soft-primary" : "btn-soft-secondary"
                } btn-sm material-shadow-none me-1`}
                onClick={() => setPeriod("1Y")}
              >
                1Y
              </button>
              <button
                type="button"
                className={`btn ${
                  period === "ALL" ? "btn-soft-primary" : "btn-soft-secondary"
                } btn-sm material-shadow-none`}
                onClick={() => setPeriod("ALL")}
              >
                ALL
              </button>
            </div>
          </CardHeader>

          <CardHeader className="p-0 border-0 bg-light-subtle">
            <Row className="g-0 text-center">
              <Col xs={6} sm={3}>
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    <span
                      className="counter-value"
                      data-target={userStats.totalUsers}
                    >
                      <CountUp
                        start={0}
                        end={userStats.totalUsers}
                        duration={3}
                      />
                    </span>
                  </h5>
                  <p className="text-muted mb-0">Total Users</p>
                </div>
              </Col>
              <Col xs={6} sm={3}>
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    <span
                      className="counter-value"
                      data-target={userStats.totalPages}
                    >
                      <CountUp
                        start={0}
                        end={userStats.totalPages}
                        duration={3}
                      />
                    </span>
                  </h5>
                  <p className="text-muted mb-0">Total Pages</p>
                </div>
              </Col>
              <Col xs={6} sm={3}>
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    <span
                      className="counter-value"
                      data-target={userStats.totalAgencies}
                    >
                      <CountUp
                        start={0}
                        end={userStats.totalAgencies}
                        duration={3}
                      />
                    </span>
                  </h5>
                  <p className="text-muted mb-0">UN Agencies</p>
                </div>
              </Col>
              <Col xs={6} sm={3}>
                <div className="p-3 border border-dashed border-start-0 border-end-0">
                  <h5 className="mb-1">
                    <span
                      className="counter-value"
                      data-target={userStats.ddSearches}
                    >
                      <CountUp
                        start={0}
                        end={userStats.ddSearches}
                        duration={3}
                      />
                    </span>
                  </h5>
                  <p className="text-muted mb-0">DD Searches</p>
                </div>
              </Col>
            </Row>
          </CardHeader>

          <CardBody className="p-0 pb-2">
            <div className="w-100">
              <ReactApexChart
                dir="ltr"
                options={options}
                series={chartData.series}
                id={chartId}
                type="line"
                height="345"
                data-colors='["--vz-success", "--vz-info", "--vz-primary"]'
                data-colors-modern='["--vz-primary", "--vz-secondary", "--vz-success"]'
                data-colors-interactive='["--vz-secondary", "--vz-info", "--vz-primary"]'
                data-colors-creative='["--vz-info", "--vz-secondary", "--vz-success"]'
                data-colors-corporate='["--vz-secondary", "--vz-success", "--vz-primary"]'
                className="apex-charts"
              />
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default ActiveUserStats;
